import { formatPrice } from "@/lib/utils";

export function buildWhatsappShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export type PartnerMessageTone =
  | "short"
  | "long"
  | "whatsapp"
  | "instagram"
  | "facebook";

export interface PartnerShareFields {
  title: string;
  price: number;
  location: string;
  defects?: string;
  /** Referral item URL (includes ?ref= when applicable). */
  url: string;
}

const CTA = "Shop on Sell It Off";

function defectsLine(defects: string | undefined): string {
  const t = defects?.trim();
  if (!t) return "";
  return `\nCondition: ${t}`;
}

/**
 * One block partners can pair with a downloaded hero image (asset kit).
 */
export function buildPartnerAssetKitCaption(ctx: PartnerShareFields): string {
  const price = formatPrice(ctx.price);
  const loc = ctx.location?.trim() ? `\n📍 ${ctx.location}` : "";
  const defects = defectsLine(ctx.defects);
  return `${ctx.title}\n${price}${loc}${defects}\n\n${CTA}\n${ctx.url}`;
}

export function buildPartnerMessage(
  tone: PartnerMessageTone,
  ctx: PartnerShareFields,
): string {
  const price = formatPrice(ctx.price);
  const loc = ctx.location?.trim() ? ` · ${ctx.location}` : "";
  const defects = defectsLine(ctx.defects);

  switch (tone) {
    case "short":
      return `${ctx.title} — ${price}${loc}\n${ctx.url}`;
    case "long":
      return `${ctx.title} is live on Sell It Off.\nPrice: ${price}${loc}${defects}\n\n${CTA}:\n${ctx.url}`;
    case "whatsapp":
      return `Hey! 👋 Found this on *Sell It Off*:\n\n*${ctx.title}*\n💰 ${price}${loc ? `\n📍 ${ctx.location}` : ""}${defects ? `\n📝 ${ctx.defects!.trim()}` : ""}\n\n👉 ${ctx.url}`;
    case "instagram":
      return `${ctx.title}\n${price}${loc ? `\n${ctx.location}` : ""}\n\n${CTA} 🔗 link in bio vibes — tap:\n${ctx.url}\n\n#SellItOff #Preloved #Deals`;
    case "facebook":
      return `Sharing a find from Sell It Off:\n\n${ctx.title}\n${price}${loc}\n${defects}\n\nSee photos & details here:\n${ctx.url}`;
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

export const PARTNER_TEMPLATE_LABELS: Record<
  PartnerMessageTone,
  { label: string; hint: string }
> = {
  short: { label: "Short", hint: "SMS / quick paste" },
  long: { label: "Long", hint: "Email / detail" },
  whatsapp: { label: "WhatsApp", hint: "Chat-friendly" },
  instagram: { label: "Instagram", hint: "Caption + tags" },
  facebook: { label: "Facebook", hint: "Feed post" },
};
